import { allFieldsOfConfig, betweenStartAndEndQueryPart, customFieldIdToQl, issueTypeQueryPart, kv, loadAll, projectQueryPart, TimedCache, WithLogger } from '@app/booking-libs';
import { Invocations } from '@app/booking-types/types';
import { invoke, requestJira } from '@forge/bridge';
export class Jira extends WithLogger {
    constructor() {
        super('JiraFromUI');
        this.cache = new TimedCache(10000);
    }
    async logWhenFail(msg, promise) {
        return promise.catch((e) => {
            invoke(Invocations.log, { message: msg, error: e, level: 'warn' });
            return [];
        });
    }
    loadProjects(force = false, projectStatues = ['live'], onlyCompanyManaged = true) {
        const key = `projects::${projectStatues.join(',')}::${onlyCompanyManaged}`;
        if (force || !this.cache.has(key)) {
            const params = new URLSearchParams();
            params.append('expand', 'issueTypes');
            for (const v of projectStatues) {
                params.append('status', v);
                params.append('action', 'edit');
            }
            this.cache.set(key, this.logWhenFail('projects failed to load', loadAll((startAt) => {
                params.set('startAt', startAt.toString());
                return requestJira(`/rest/api/3/project/search?${params}`);
            }).then((projects) => projects.filter((project) => (onlyCompanyManaged ? !project.simplified : true)))));
        }
        return this.cache.get(key);
    }
    loadCustomFields(force = false) {
        const key = 'customFields';
        if (force || !this.cache.has(key)) {
            this.cache.set(key, this.logWhenFail('customfields failed to load', loadAll(async (startAt) => requestJira(`/rest/api/3/field/search?startAt=${startAt}&type=custom`))));
        }
        return this.cache.get(key);
    }
    loadStatuses(force = false) {
        const key = 'statuses';
        if (force || !this.cache.has(key)) {
            const params = new URLSearchParams();
            params.append('expand', 'usages');
            this.cache.set(key, this.logWhenFail('Failed to load statuses', loadAll((startAt) => {
                params.set('startAt', startAt.toString());
                return requestJira(`/rest/api/3/statuses/search?${params}`);
            })));
        }
        return this.cache.get(key);
    }
    async loadIssueLinkTypes(force = false) {
        const key = 'issueLinkTypes';
        if (force || !this.cache.has(key)) {
            this.cache.set(key, this.logWhenFail('Failed to load issue link types', requestJira('/rest/api/3/issueLinkType', {
                headers: { Accept: 'application/json' }
            })
                .then((response) => {
                if (!response.ok) {
                    this.logger.warn('Failed to load issue link types', response.statusText, response.status);
                    return [];
                }
                return response.json();
            })
                .then((json) => json.issueLinkTypes)));
        }
        return this.cache.get(key);
    }
    async getStatusesForProjectAndIssuetype(projectId, issuetypeId, force = false) {
        const key = `statuses::${projectId}`;
        if (force || !this.cache.has(key)) {
            async function run() {
                const result = await requestJira(`/rest/api/3/project/${projectId}/statuses`);
                if (result.ok) {
                    return await result.json();
                }
                return [];
            }
            this.cache.set(key, this.logWhenFail(`Failed to load statuses for project ${projectId}`, run()));
        }
        const issuetypes = (await this.cache.get(key));
        const issuetype = issuetypes.find((type) => type.id === issuetypeId);
        if (!issuetype) {
            return [];
        }
        return issuetype.statuses;
    }
    getWorkflowScheme(force = false) {
        const key = 'workflowScheme';
        if (force || !this.cache.has(key)) {
            this.cache.set(key, this.logWhenFail('Failed to load workflow schemes', loadAll(async (startAt) => requestJira(`/rest/api/3/workflowscheme?startAt=${startAt}`))));
        }
        return this.cache.get(key);
    }
    async getWorkflowForIssuetype(issuetypeId, force = false) {
        const workflowSchemes = await this.getWorkflowScheme(force);
        const eligibleWorkflowNames = workflowSchemes
            .filter((scheme) => scheme.issueTypeMappings[issuetypeId])
            .map((scheme) => scheme.issueTypeMappings[issuetypeId]);
        return (await this.getWorkflows(force)).filter((wf) => eligibleWorkflowNames.includes(wf.id.name));
    }
    getWorkflows(force = false) {
        const key = 'workflows';
        if (force || !this.cache.has(key)) {
            const params = new URLSearchParams();
            params.append('expand', 'transitions');
            params.append('expand', 'projects');
            params.append('expand', 'statuses');
            params.append('isActive', 'true');
            this.cache.set(key, this.logWhenFail('Failed to load workflows', loadAll((startAt) => {
                params.set('startAt', startAt.toString());
                return requestJira(`/rest/api/3/workflow/search?${params}`);
            })));
        }
        return this.cache.get(key);
    }
    getCreationFields(projectId, issuetypeId, force = true) {
        const key = `meta:fields:${projectId}:${issuetypeId}`;
        if (force || !this.cache.has(key)) {
            this.cache.set(key, this.logWhenFail(`Failed to fetch fields for create screen for project (${projectId}) and issuetypeid(${issuetypeId})`, loadAll((startAt) => requestJira(`/rest/api/3/issue/createmeta/${projectId}/issuetypes/${issuetypeId}?startAt=${startAt}`), 'fields')));
        }
        return this.cache.get(key);
    }
    async checkIfJiraAdmin() {
        const key = 'isJiraAdmin';
        if (!this.cache.has(key)) {
            this.cache.set(key, requestJira('/rest/api/3/mypermissions?permissions=ADMINISTER')
                .then((r) => {
                if (r.ok) {
                    return r.json().then((json) => json.permissions.ADMINISTER.havePermission);
                }
                this.logger.error('Could not find permission result');
                return false;
            })
                .catch((err) => {
                this.logger.debug('There was an error when fetching permission result (might be normal if anonymous access)');
                return false;
            }));
        }
        return this.cache.get(key);
    }
    async fetchIssuesFromConfiguration(config, { sortBy, sortAsc, startDate, endDate, fieldValues, statusesIds, reportersIds, excludedWorkItemIds, reconcileIssues }, timezone) {
        const jqlArray = [projectQueryPart(config.projectId), issueTypeQueryPart(config.issuetypeId)];
        if (startDate && endDate) {
            jqlArray.push(betweenStartAndEndQueryPart(config.startFieldId, config.endFieldId, startDate, endDate, timezone));
        }
        if (fieldValues === null || fieldValues === void 0 ? void 0 : fieldValues.length) {
            jqlArray.push(`${customFieldIdToQl(config.resourceFieldId)} IN (${fieldValues.join(', ')})`);
        }
        if (statusesIds === null || statusesIds === void 0 ? void 0 : statusesIds.length) {
            jqlArray.push(`status in (${statusesIds.join(', ')})`);
        }
        if (reportersIds === null || reportersIds === void 0 ? void 0 : reportersIds.length) {
            jqlArray.push(`reporter in (${reportersIds.join(', ')})`);
        }
        if (excludedWorkItemIds === null || excludedWorkItemIds === void 0 ? void 0 : excludedWorkItemIds.length) {
            jqlArray.push(`issuekey not in (${excludedWorkItemIds.join(', ')})`);
        }
        const jql = `${jqlArray.join(' AND ')} ORDER BY ${customFieldIdToQl(sortBy ? sortBy : config.startFieldId)} ${sortAsc ? 'ASC' : 'DESC'}`;
        const fields = allFieldsOfConfig(config);
        const payload = {
            jql,
            fields: [...fields, 'status', 'summary', 'reporter']
        };
        if (reconcileIssues === null || reconcileIssues === void 0 ? void 0 : reconcileIssues.length) {
            payload.reconcileIssues = reconcileIssues;
        }
        const rSearch = await requestJira('/rest/api/3/search/jql', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        if (!rSearch.ok) {
            this.logger.warn(`Failed to fetch issues from configuration ${rSearch.status}`, await rSearch.json());
            return [];
        }
        const result = (await rSearch.json());
        return result.issues;
    }
    async getFieldOptions(fieldIdToFind, projectId, issuetypeId) {
        const key = `fieldOptions-${fieldIdToFind}-${projectId}-${issuetypeId}`;
        if (!this.cache.has(key)) {
            this.cache.set(key, this.getCreationFields(projectId, issuetypeId).then((fields) => {
                const field = fields.find(({ fieldId }) => fieldId === fieldIdToFind);
                if (!field) {
                    this.logger.warn(`Failed to fetch field options for ${fieldIdToFind} (${projectId}:${issuetypeId})`);
                    return [];
                }
                if (field.schema.type !== 'option') {
                    this.logger.warn(`Field ${field.name} (${fieldIdToFind}) is not of correct type`, field.schema);
                    return [];
                }
                return field.allowedValues;
            }));
        }
        return this.cache.get(key);
    }
    async getIssueDetails(key) {
        try {
            const response = await requestJira(`/rest/api/3/issue/${key}`, {
                headers: {
                    Accept: 'application/json'
                }
            });
            if (!response.ok) {
                this.logger.warn(`Failed to fetch  ${kv('issueKey', key)}`);
                return undefined;
            }
            return (await response.json());
        }
        catch (e) {
            this.logger.warn(`Failed to fetch ${kv('issueKey', key)}`);
            return undefined;
        }
    }
    async getUser(accountId) {
        try {
            const response = await requestJira(`/rest/api/3/user/search?accountId=${accountId}`, {
                headers: { Accept: 'application/json' }
            });
            return (await response.json())[0];
        }
        catch (e) {
            this.logger.warn(`Failed to fetch the ${kv('accountId', accountId)}`);
        }
    }
    async getIssuetypes() {
        try {
            const response = await requestJira('/rest/api/3/issuetype', {
                headers: { Accept: 'application/json' }
            });
            return (await response.json());
        }
        catch (e) {
            this.logger.warn('Failed to fetch issuetypes');
            return [];
        }
    }
}
